/**
 * Maps ai-companion edge function actions to dashboard navigation + user messaging.
 */

export type CompanionDashboardPage =
  | 'dashboard'
  | 'jobs'
  | 'history'
  | 'my-activities'
  | 'resume-builder'
  | 'prepare-for-interview'
  | 'skill-assessment'
  | 'profile'
  | 'credits'
  | 'calendar-notes'
  | 'objectives-tasks'
  | 'agent'
  | 'ai-companion'
  | 'collaborate';

const EDGE_PAGE_TO_DASHBOARD: Record<string, CompanionDashboardPage> = {
  dashboard: 'dashboard',
  jobs: 'jobs',
  history: 'my-activities',
  'resume-builder': 'resume-builder',
  'prepare-for-interview': 'prepare-for-interview',
  'skill-assessment': 'skill-assessment',
  profile: 'profile',
  credits: 'credits',
  'calendar-notes': 'calendar-notes',
  'objectives-tasks': 'objectives-tasks',
  'single-optimize': 'agent',
  agent: 'agent',
  'ai-companion': 'ai-companion',
  collaborate: 'collaborate',
};

export function mapCompanionNavigatePage(raw: string): CompanionDashboardPage | null {
  const key = raw.trim().toLowerCase();
  return EDGE_PAGE_TO_DASHBOARD[key] ?? null;
}

/** URL path for dashboard navigation from ai-companion navigate actions. */
export function companionPageToPath(
  page: CompanionDashboardPage,
  currentPath = typeof window !== 'undefined' ? window.location.pathname : '',
): string {
  const paths: Record<CompanionDashboardPage, string> = {
    dashboard: '/dashboard',
    jobs: '/jobs',
    history: '/history',
    'my-activities': '/history',
    'resume-builder': '/resume-builder',
    'prepare-for-interview': '/prepare-for-interview',
    'skill-assessment': '/skill-assessment',
    profile: '/account-settings',
    credits: '/credits',
    'calendar-notes': '/calendar-notes',
    'objectives-tasks': '/objectives-tasks',
    agent: '/agent',
    'ai-companion': '/ai-companion',
    collaborate: '/collaborate',
  };

  return paths[page] ?? '/dashboard';
}

export type CompanionActionResult = {
  /** Extra assistant line appended after edge content (if any). */
  followUp?: string;
  /** Navigate after showing the message. */
  navigateTo?: CompanionDashboardPage;
  /** Persist a prompt for /agent on next load. */
  agentPrompt?: string;
};

export function resolveCompanionAction(
  action: string,
  actionData: Record<string, unknown> | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): CompanionActionResult {
  const page = typeof actionData?.page === 'string' ? actionData.page : undefined;

  if (action === 'navigate' && page) {
    const mapped = mapCompanionNavigatePage(page);
    if (mapped) {
      return {
        followUp: t('dashboard.aiCompanion.actionNavigating', { page: mapped }),
        navigateTo: mapped,
      };
    }
    return { followUp: t('dashboard.aiCompanion.actionNavigateUnknown', { page }) };
  }

  if (action === 'web_search') {
    return {
      followUp: t('dashboard.aiCompanion.actionUseAgentForWeb'),
    };
  }

  if (action === 'cover_letter' || action === 'application_letter') {
    const jobTitle = typeof actionData?.jobTitle === 'string' ? actionData.jobTitle : '';
    const company = typeof actionData?.company === 'string' ? actionData.company : '';
    const prompt = jobTitle
      ? `Draft a ${action === 'cover_letter' ? 'cover letter' : 'application letter'} for ${jobTitle}${company ? ` at ${company}` : ''}.`
      : `Help me draft a ${action === 'cover_letter' ? 'cover letter' : 'application letter'}.`;
    return {
      followUp: t('dashboard.aiCompanion.actionOpenAgentForLetter'),
      agentPrompt: prompt,
    };
  }

  if (action === 'optimize_resume') {
    return {
      followUp: t('dashboard.aiCompanion.actionOpenAgentForOptimize'),
      agentPrompt: 'Help me optimize my resume for a specific job.',
    };
  }

  if (action === 'record_application') {
    return {
      followUp: t('dashboard.aiCompanion.actionOpenHistory'),
      navigateTo: 'my-activities',
    };
  }

  return {};
}

/** BCP-47 tag for Web Speech API from app + voice settings. */
export function getSpeechRecognitionLang(
  appLanguage: string,
  preferIndianVoices: boolean,
  voiceAccent?: string,
): string {
  if (preferIndianVoices || voiceAccent === 'indian') return 'hi-IN';
  const key = (appLanguage || 'en').split('-')[0].toLowerCase();
  const map: Record<string, string> = {
    en: 'en-US',
    pt: 'pt-BR',
    es: 'es-ES',
    ru: 'ru-RU',
    hi: 'hi-IN',
  };
  return map[key] ?? 'en-US';
}

/** Voice fetch / LLM response language code. */
export function getCompanionVoiceLanguage(
  appLanguage: string,
  preferIndianVoices: boolean,
  isEnglishApp: boolean,
): string {
  if (isEnglishApp && preferIndianVoices) return 'hi';
  const key = (appLanguage || 'en').split('-')[0].toLowerCase();
  if (['en', 'pt', 'es', 'ru', 'hi'].includes(key)) return key;
  return 'en';
}
